import { useState, useCallback } from "react";
import {
  Box,
  type SxProps,
  type Theme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Typography from "@mui/material/Typography";
import { NewsSummaryAPI } from "@/features/mypage/api";
import { News } from "@/features/mypage/components/News";
import { NewsDetailDialog } from "@/features/mypage/components/NewsDetailDialog";
import { theme as appTheme } from "@/types/theme";

const MAX_VISIBLE_NEWS = 10;

export interface NewsContainerProps {
  title: string;
  year: number;
  pinnedOnly?: boolean;
}

export const NewsSummaryContainer: React.FC<NewsContainerProps> = (props) => {
  const [expanded, setExpanded] = useState(false);
  const handleAccordionChange = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);
  const [selectedNewsId, setSelectedNewsId] = useState<number | null>(null);
  const newsResponse = NewsSummaryAPI(props.year);
  const newsSummaries = props.pinnedOnly
    ? newsResponse.pinned
    : newsResponse.all;

  // pinnedOnlyモードでpinnedのニュースがなければ非表示
  if (props.pinnedOnly && newsSummaries.length === 0) {
    return null;
  }
  const containerSx: SxProps<Theme> = {
    mb: 0,
    bgcolor: "#FFFFFF",
    padding: "0.5em",
    borderRadius: "0.5em",
  };
  const headerSx: SxProps<Theme> = {
    mb: 0,
  };

  const titleSx: SxProps<Theme> = {
    fontSize: "16px",
    fontWeight: 500,
    color: appTheme.palette.black.main,
    padding: "0.5em",
  };

  const noNewsSx: SxProps<Theme> = {
    fontSize: "16px",
    color: appTheme.palette.black.main,
    padding: "0.5em",
  };

  return (
    <Box sx={containerSx}>
      {props.title && (
        <Box sx={headerSx}>
          <Typography sx={titleSx}>{props.title}</Typography>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: "8px", md: 0 },
        }}
      >
        {(newsSummaries?.length ?? 0) === 0 ? (
          <Typography sx={noNewsSx}>ニュースはありません。</Typography>
        ) : (
          <>
            {newsSummaries.slice(0, MAX_VISIBLE_NEWS).map((news) => (
              <News
                key={news.id}
                {...news}
                onClick={() => {
                  setSelectedNewsId(news.id);
                }}
              />
            ))}
            {newsSummaries.length > MAX_VISIBLE_NEWS && (
              <Accordion
                expanded={expanded}
                onChange={handleAccordionChange}
                sx={{
                  boxShadow: "none",
                  "&:before": { display: "none" },
                  backgroundColor: "transparent",
                  display: "flex",
                  flexDirection: "column-reverse",
                  margin: "0 !important",
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{
                        fontSize: "14px",
                        color: appTheme.palette.primary.main,
                      }}
                    />
                  }
                  sx={{
                    minHeight: "auto !important",
                    padding: "8px 16px",
                    justifyContent: "center",
                    "&.Mui-expanded": {
                      minHeight: "auto !important",
                    },
                    "& .MuiAccordionSummary-content": {
                      margin: 0,
                      flexGrow: 0,
                    },
                    "& .MuiAccordionSummary-content.Mui-expanded": {
                      margin: 0,
                    },
                    "& .MuiAccordionSummary-expandIconWrapper": {
                      marginLeft: "4px",
                    },
                    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                      transform: "rotate(180deg)",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      color: appTheme.palette.primary.main,
                      fontSize: "14px",
                    }}
                  >
                    {expanded
                      ? "閉じる"
                      : `さらに${
                          newsSummaries.length - MAX_VISIBLE_NEWS
                        }件を表示`}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  {newsSummaries.slice(MAX_VISIBLE_NEWS).map((news) => (
                    <News
                      key={news.id}
                      {...news}
                      onClick={() => {
                        setSelectedNewsId(news.id);
                      }}
                    />
                  ))}
                </AccordionDetails>
              </Accordion>
            )}
          </>
        )}
      </Box>
      <NewsDetailDialog
        newsId={selectedNewsId}
        onClose={() => {
          setSelectedNewsId(null);
        }}
      />
    </Box>
  );
};
